import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import "../../constants/colors.css";
import "../../constants/fonts.css";
import "../../constants/scrollbar.css";
import "../../constants/typography.css";
import { setTimeZone } from "../../features/timeZoneSlice/timeZoneSlice";
import {
  getAdminRolePermission,
  logout,
  setUser,
} from "../../features/user/userSlice";
import useSocketConnection from "../../hooks/useSocketConnection";
import ConfirmationModal from "../confirmationModal/ConfirmationModal";
import Header from "../header/Header";
import StatusModalStack from "../statusModal/StatusModalStack";
import styles from "./appContainer.module.css";
import axios from "axios";
import { serverLink } from "../../api/serverLink";
import {
  isMongoObjectIdString,
  syncDashboardAdminIdFromAccessToken,
} from "../../utils/adminSessionFromToken";

const AppContainer = ({ children }) => {
  const [isInitialized, setIsInitialized] = useState(false);

  const { token } = useSelector((state) => state.user);

  const dispatch = useDispatch();

  useEffect(() => {
    const initAuth = async () => {
      const refreshToken = localStorage.getItem("refreshToken");
      const adminName = localStorage.getItem("adminName");

      if (!refreshToken) {
        dispatch(logout());
        setIsInitialized(true);
        return;
      }

      // We have a refresh token — try to get a fresh access token
      const storedAccessToken = localStorage.getItem("authToken");

      if (storedAccessToken) {
        syncDashboardAdminIdFromAccessToken(storedAccessToken);
        const resolvedAdminId = localStorage.getItem("adminId");
        // Optimistically restore state; the interceptor will handle refresh
        // if the access token turns out to be expired on first API call
        dispatch(
          setUser({
            userName: adminName,
            userId: resolvedAdminId,
            token: storedAccessToken,
            refreshToken,
          }),
        );
        setIsInitialized(true);
        return;
      }

      // No access token but have refresh token — attempt refresh
      try {
        const { data } = await axios.post(
          `${serverLink}/api/auth/refresh-token`,
          { refreshToken },
        );

        const newAccessToken = data.data.accessToken;
        const newRefreshToken = data.data.refreshToken;

        localStorage.setItem("authToken", newAccessToken);
        localStorage.setItem("refreshToken", newRefreshToken);
        syncDashboardAdminIdFromAccessToken(newAccessToken);

        dispatch(
          setUser({
            userName: adminName,
            userId: localStorage.getItem("adminId"),
            token: newAccessToken,
            refreshToken: newRefreshToken,
          }),
        );
      } catch {
        // Refresh failed — force logout
        localStorage.removeItem("authToken");
        localStorage.removeItem("refreshToken");
        localStorage.removeItem("adminId");
        localStorage.removeItem("adminName");
        dispatch(logout());
      }

      setIsInitialized(true);
    };

    initAuth();

    const userTimeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
    dispatch(setTimeZone(userTimeZone));
  }, [dispatch]);

  // Every new token re-reads the permissions. That's also how a role change
  // reaches an open dashboard: the backend drops this admin's socket, and the
  // reconnect refreshes the token first (sockets/webSocketService.js).
  useEffect(() => {
    if (!token) return;
    const accessToken =
      typeof token === "string" ? token : localStorage.getItem("authToken");
    syncDashboardAdminIdFromAccessToken(accessToken);
    const adminId = localStorage.getItem("adminId");
    if (isMongoObjectIdString(adminId || "")) {
      dispatch(getAdminRolePermission(adminId));
    }
  }, [dispatch, token]);

  useSocketConnection();

  if (!isInitialized) return null;

  return (
    <div className={styles.container}>
      <Header />
      <div className={styles.content}>{children}</div>
      <StatusModalStack />
      <ConfirmationModal />
    </div>
  );
};

export default AppContainer;
